import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useStore } from '../store/useStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import MailSidebar from '../components/mail/MailSidebar';
import IntroCarpetas from '../components/mail/IntroCarpetas';
import MessageList from '../components/mail/MessageList';
import Reader from '../components/mail/Reader';
import Composer from '../components/mail/Composer';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { Mail, PenSquare, X } from 'lucide-react';
import { initials as initialsOf } from '../lib/format';

export default function MailApp() {
  const isMobile = useIsMobile();
  const {
    activeAccountId, setActiveAccount, composer, selectedUid, fullscreen, setFullscreen, openComposer,
    mobileFoldersOpen, setMobileFoldersOpen, openMessage, setActiveFolder, user,
  } = useStore();
  // Redactar se encoge a solo el lápiz mientras se baja por la lista y vuelve a
  // su forma larga al subir (como Gmail en Android): no tapa los correos.
  const [fabCompacto, setFabCompacto] = useState(false);
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const res = await api.get('/accounts');
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.accounts)) return res.data.accounts;
      return [];
    },
  });

  useEffect(() => {
    if (accounts.length && (!activeAccountId || !accounts.find((a) => a.id === activeAccountId))) {
      const def = accounts.find((a) => a.is_default) || accounts[0];
      setActiveAccount(def.id);
    }
  }, [accounts, activeAccountId, setActiveAccount]);

  useEffect(() => { if (isMobile && fullscreen) setFullscreen(false); }, [isMobile, fullscreen, setFullscreen]);

  useEffect(() => {
    if (!isMobile) return undefined;
    const ultimo = new WeakMap();
    const alDesplazar = (e) => {
      const el = e.target;
      if (!el || typeof el.scrollTop !== 'number') return;
      const antes = ultimo.get(el) ?? el.scrollTop;
      const delta = el.scrollTop - antes;
      ultimo.set(el, el.scrollTop);
      if (el.scrollTop < 24) setFabCompacto(false);
      else if (delta > 6) setFabCompacto(true);
      else if (delta < -6) setFabCompacto(false);
    };
    window.addEventListener('scroll', alDesplazar, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', alDesplazar, { capture: true });
  }, [isMobile]);

  // Enlace profundo desde una notificación (push con la app cerrada abre
  // /mail?a=<cuenta>&f=<carpeta>&u=<uid>). Abrimos ese correo y limpiamos la URL
  // para que un F5 posterior no reabra siempre el mismo mensaje.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const a = p.get('a'); const u = p.get('u'); const f = p.get('f') || 'inbox';
    if (!a || !u) return;
    setActiveAccount(a);
    setActiveFolder(f);
    openMessage(a, f, u);
    window.history.replaceState({}, '', window.location.pathname);
  }, [openMessage, setActiveFolder, setActiveAccount]);

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center" style={{ color: 'var(--gmail-text-sec)' }}>
        Cargando…
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="h-full grid place-items-center p-6" style={{ background: 'var(--gmail-bg)' }}>
        <div className="text-center max-w-md bg-white dark:bg-[var(--gmail-surface)] rounded-3xl p-10 shadow-sm border" style={{ borderColor: 'var(--border)' }}>
          <div className="h-16 w-16 rounded-full grid place-items-center mx-auto mb-4" style={{ background: 'var(--gmail-selected)' }}>
            <Mail className="h-8 w-8" style={{ color: 'var(--gmail-blue)' }} />
          </div>
          <h2 className="text-2xl font-normal mb-2" style={{ color: 'var(--gmail-text)' }}>Preparando tu buzón</h2>
          <p className="text-sm" style={{ color: 'var(--gmail-text-sec)' }}>
            Tu buzón de Orvian se conecta automáticamente al iniciar sesión.
            Si esto no cambia en unos segundos, cierra sesión y vuelve a entrar.
          </p>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-full relative mobile-mail-root" style={{ background: 'var(--gmail-bg)' }}>
        <IntroCarpetas />
        {!selectedUid ? (
          <MessageList mobile />
        ) : (
          <Reader mobile />
        )}
        {/* Single folders drawer — controlled from AppShell hamburger only */}
        <Sheet open={mobileFoldersOpen} onOpenChange={setMobileFoldersOpen}>
          <SheetContent
            side="left"
            sinCerrar
            overlayClassName="bg-black/45 backdrop-blur-[2px]"
            className="p-0 w-[min(20rem,86vw)] border-0 max-w-full mobile-drawer outline-none focus:outline-none focus-visible:outline-none"
            aria-describedby={undefined}
            // Sin foco automático en la X: al abrir con el dedo pintaba un anillo de foco enorme.
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="h-full flex flex-col">
              <div className="mobile-drawer-head">
                <div className="flex items-center gap-[9px] min-w-0">
                  <span className="od-brand-mark" aria-hidden="true">O</span>
                  <SheetTitle className="mobile-drawer-brand">Orvian</SheetTitle>
                </div>
                <button type="button" className="gmail-icon-btn" onClick={() => setMobileFoldersOpen(false)} aria-label="Cerrar menú" data-testid="mobile-drawer-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {(() => {
                const cuenta = accounts.find((a) => a.id === activeAccountId) || accounts[0];
                const correo = cuenta?.email || user?.email || '';
                const nombre = user?.name || cuenta?.display_name || correo.split('@')[0];
                return (
                  <div className="mobile-drawer-account" data-testid="mobile-drawer-account">
                    <span className="mobile-drawer-avatar" aria-hidden="true">{initialsOf(nombre, correo)}</span>
                    <div className="min-w-0">
                      <div className="mobile-drawer-name">{nombre}</div>
                      <div className="mobile-drawer-mail">{correo}</div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex-1 min-h-0">
                <MailSidebar accounts={accounts} onNavigate={() => setMobileFoldersOpen(false)} />
              </div>
            </div>
          </SheetContent>
        </Sheet>
        {!selectedUid && !composer.open && (
          <button
            type="button"
            className={`compose-fab-mobile ${fabCompacto ? 'compose-fab-mobile--compacto' : ''}`}
            onClick={() => openComposer('new')}
            aria-label="Redactar"
            data-testid="mail-compose-fab"
          >
            <PenSquare className="h-[22px] w-[22px] shrink-0" strokeWidth={2} />
            <span className="compose-fab-label">Redactar</span>
          </button>
        )}
        {composer.open && <Composer accounts={accounts} mobile />}
      </div>
    );
  }

  // Desktop Gmail: sidebar | list | reader on soft gray canvas
  return (
    <div className="h-full flex relative" style={{ background: 'var(--gmail-bg)' }}>
      <IntroCarpetas />
      {!fullscreen && (
        <MailSidebar accounts={accounts} />
      )}
      <div className="flex-1 min-w-0 h-full pr-2 pb-2 pt-0">
        {fullscreen ? (
          <div className="h-full rounded-2xl overflow-hidden" data-testid="mail-viewer-col" style={{ background: 'var(--gmail-surface)' }}>
            <Reader />
          </div>
        ) : !selectedUid ? (
          /* Sin correo abierto la lista ocupa todo el ancho: un panel de lectura
             vacío desperdiciaba más de la mitad de la pantalla y obligaba a
             cortar asuntos y vistas previas que sí caben. */
          <div className="h-full rounded-2xl overflow-hidden" data-testid="mail-list-col" style={{ background: 'var(--gmail-surface)' }}>
            <MessageList />
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" autoSaveId="zuno-mail-split-gmail" className="h-full gap-0">
            <ResizablePanel defaultSize={38} minSize={28} maxSize={55} className="min-w-[320px]">
              <div className="h-full mr-2 rounded-2xl overflow-hidden" data-testid="mail-list-col" style={{ background: 'var(--gmail-surface)' }}>
                <MessageList />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle data-testid="mail-resize-handle" className="bg-transparent w-2" />
            <ResizablePanel defaultSize={62} minSize={35}>
              <div className="h-full rounded-2xl overflow-hidden" data-testid="mail-viewer-col" style={{ background: 'var(--gmail-surface)' }}>
                <Reader />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
      {composer.open && <Composer accounts={accounts} />}
    </div>
  );
}
